import { Vec2 } from '../math/vec2.js';
import { ARENA_WIDTH, ARENA_HEIGHT, SHIP_MAX_HP, ASTEROID_SMALL_RADIUS, ASTEROID_MEDIUM_RADIUS, ASTEROID_LARGE_RADIUS, ASTEROID_SMALL_HP, ASTEROID_MEDIUM_HP, ASTEROID_LARGE_HP, ASTEROID_MAX_SPEED, ASTEROID_SPIN_SPEED, PROJECTILE_SPEED, PROJECTILE_LIFETIME, CRATE_SCATTER_SPEED, STATION_WAYPOINT_MARGIN, MATCH_DURATION_SECONDS, } from '../constants/game.js';
/**
 * Create a new ship for a player
 */
export function createShip(id, playerId, spawnPos) {
    // Default spawn positions around the arena
    const defaultSpawns = [
        new Vec2(ARENA_WIDTH * 0.2, ARENA_HEIGHT * 0.2),
        new Vec2(ARENA_WIDTH * 0.8, ARENA_HEIGHT * 0.2),
        new Vec2(ARENA_WIDTH * 0.2, ARENA_HEIGHT * 0.8),
        new Vec2(ARENA_WIDTH * 0.8, ARENA_HEIGHT * 0.8),
        new Vec2(ARENA_WIDTH * 0.5, ARENA_HEIGHT * 0.2),
        new Vec2(ARENA_WIDTH * 0.5, ARENA_HEIGHT * 0.8),
        new Vec2(ARENA_WIDTH * 0.2, ARENA_HEIGHT * 0.5),
        new Vec2(ARENA_WIDTH * 0.8, ARENA_HEIGHT * 0.5),
    ];
    const pos = spawnPos || defaultSpawns[playerId % defaultSpawns.length];
    return {
        id,
        playerId,
        pos,
        vel: Vec2.zero(),
        rot: -Math.PI / 2, // Pointing up
        tailSegments: [],
        alive: true,
        respawnTimer: 0,
        fireCooldown: 0,
        score: 0,
        totalOreTurnedIn: 0,
        kills: 0,
        isNpc: false,
        npcAi: null,
        hp: SHIP_MAX_HP,
        maxHp: SHIP_MAX_HP,
        damageLevel: 0,
        invulnerableTimer: 0,
    };
}
/**
 * Create NPC AI state
 */
export function createNpcAi() {
    return {
        state: 'mining',
        targetId: null,
        targetPos: null,
        stateTimer: 0,
        decisionCooldown: 0,
        aggressiveness: 0.3 + Math.random() * 0.4, // 0.3-0.7
        skillLevel: 0.4 + Math.random() * 0.4, // 0.4-0.8
    };
}
/**
 * Create a new NPC ship
 */
export function createNpcShip(id, playerId, spawnPos) {
    const ship = createShip(id, playerId, spawnPos);
    ship.isNpc = true;
    ship.npcAi = createNpcAi();
    return ship;
}
/**
 * Create a new asteroid
 */
export function createAsteroid(id, pos, size, vel) {
    const radiusMap = {
        small: ASTEROID_SMALL_RADIUS,
        medium: ASTEROID_MEDIUM_RADIUS,
        large: ASTEROID_LARGE_RADIUS,
    };
    const hpMap = {
        small: ASTEROID_SMALL_HP,
        medium: ASTEROID_MEDIUM_HP,
        large: ASTEROID_LARGE_HP,
    };
    // Random velocity if not specified
    const velocity = vel || Vec2.random().scale(Math.random() * ASTEROID_MAX_SPEED);
    return {
        id,
        pos,
        vel: velocity,
        rot: Math.random() * Math.PI * 2,
        angularVel: (Math.random() - 0.5) * ASTEROID_SPIN_SPEED * 2,
        size,
        hp: hpMap[size],
        maxHp: hpMap[size],
        radius: radiusMap[size],
    };
}
/**
 * Create a random asteroid somewhere in the arena
 */
export function createRandomAsteroid(id) {
    const sizes = ['small', 'medium', 'large'];
    const weights = [0.5, 0.35, 0.15]; // More small asteroids
    // Weighted random selection
    const rand = Math.random();
    let cumulative = 0;
    let size = 'small';
    for (let i = 0; i < sizes.length; i++) {
        cumulative += weights[i];
        if (rand < cumulative) {
            size = sizes[i];
            break;
        }
    }
    // Random position with margin from edges
    const margin = 100;
    const pos = new Vec2(margin + Math.random() * (ARENA_WIDTH - margin * 2), margin + Math.random() * (ARENA_HEIGHT - margin * 2));
    return createAsteroid(id, pos, size);
}
/**
 * Create a projectile
 */
export function createProjectile(id, pos, angle, ownerId) {
    const vel = Vec2.fromAngle(angle).scale(PROJECTILE_SPEED);
    return {
        id,
        pos,
        vel,
        ownerId,
        lifetime: PROJECTILE_LIFETIME,
    };
}
/**
 * Create a crate
 */
export function createCrate(id, pos, vel, value = 1) {
    // Random scatter velocity if not specified
    const velocity = vel || Vec2.random().scale(CRATE_SCATTER_SPEED * Math.random());
    return {
        id,
        pos,
        vel: velocity,
        value,
        pickupCooldown: 0,
        lastOwnerId: null,
    };
}
/**
 * Create crates from a destroyed asteroid
 */
export function createCratesFromAsteroid(startId, asteroidPos, asteroidSize) {
    const countMap = {
        small: 1,
        medium: 3,
        large: 6,
    };
    const count = countMap[asteroidSize];
    const crates = [];
    for (let i = 0; i < count; i++) {
        const angle = (i / count) * Math.PI * 2 + Math.random() * 0.5;
        const speed = CRATE_SCATTER_SPEED * (0.5 + Math.random() * 0.5);
        const vel = Vec2.fromAngle(angle).scale(speed);
        // Slight offset from center
        const offset = Vec2.random().scale(10);
        const pos = asteroidPos.add(offset);
        crates.push(createCrate(startId + i, pos, vel));
    }
    return crates;
}
/**
 * Create the station
 */
export function createStation() {
    const pos = new Vec2(ARENA_WIDTH / 2, ARENA_HEIGHT / 2);
    const targetPos = getRandomStationWaypoint();
    return {
        pos,
        vel: Vec2.zero(),
        targetPos,
        waypointTimer: 0,
    };
}
/**
 * Get a random waypoint for the station
 */
export function getRandomStationWaypoint() {
    return new Vec2(STATION_WAYPOINT_MARGIN + Math.random() * (ARENA_WIDTH - STATION_WAYPOINT_MARGIN * 2), STATION_WAYPOINT_MARGIN + Math.random() * (ARENA_HEIGHT - STATION_WAYPOINT_MARGIN * 2));
}
/**
 * Create initial world state
 */
export function createWorld(numPlayers = 1, numAsteroids = 15, numNpcs = 0) {
    let nextId = 1;
    // Create ships
    const ships = [];
    for (let i = 0; i < numPlayers; i++) {
        ships.push(createShip(nextId++, i));
    }
    // Create NPC ships
    for (let i = 0; i < numNpcs; i++) {
        const npcPlayerId = numPlayers + i;
        ships.push(createNpcShip(nextId++, npcPlayerId));
    }
    // Create asteroids
    const asteroids = [];
    for (let i = 0; i < numAsteroids; i++) {
        asteroids.push(createRandomAsteroid(nextId++));
    }
    return {
        tick: 0,
        timeRemaining: MATCH_DURATION_SECONDS,
        ships,
        asteroids,
        crates: [],
        projectiles: [],
        station: createStation(),
        effects: [],
        nextEntityId: nextId,
    };
}
/**
 * Get next entity ID from world
 */
export function getNextEntityId(world) {
    return world.nextEntityId++;
}
//# sourceMappingURL=factories.js.map